// src/pages/Attendance/HomeTab.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllTodayAttendance,
  fetchMonthlyReport,
} from "../../store/slices/attendanceSlice";
import { fetchLeaveBalance } from "../../store/slices/leaveSlice";
import { RootDispatch, RootState } from "../../store";
import {
  Users,
  TrendingUp,
  UserCheck,
  Activity,
  UserX,
  MapPin,
  Clock,
} from "lucide-react";
import AttendanceCalendar from "./MonthlyCalendar";
import { ShowUserRouteMap } from "./ShowUserRouteMap";
import { getStatusBadge, getStatusIcon } from "../../utils/attendanceUtils";
import { AttendanceRecord } from "../../models/attendance.model";
import ManagerPendingApplications from "./ManagerPendingApplications";
import BaseButton from "../../components/Assets/BaseButton";
import SearchInput from "../../components/Search/SearchInput";
import HrDashboardStack from "./HrDashboardStack";

const HomeTab: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const { allTodayRecords, monthlyRecords, loading } = useSelector(
    (state: RootState) => state.attendance
  );
  const { role } = useSelector((state: RootState) => state.auth);

  // Filters & Stats
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Calendar modal
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [monthlyMap, setMonthlyMap] = useState<
    Record<string, AttendanceRecord>
  >({});

  // Route map modal
  const [mapUserId, setMapUserId] = useState<string | null | undefined>(null);
  const [isMapOpen, setIsMapOpen] = useState(false);

  // Fetch today's attendance
  useEffect(() => {
    dispatch(fetchAllTodayAttendance({ date: selectedDate }));
  }, [dispatch, selectedDate]);

  useEffect(() => {
    if (!searchTerm) {
      dispatch(fetchAllTodayAttendance({ date: selectedDate }));
    }
  }, [searchTerm, dispatch, selectedDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    dispatch(
      fetchAllTodayAttendance({ search: searchTerm, date: e.target.value })
    );
  };

  // (Optional) preload leave balance
  useEffect(() => {
    dispatch(fetchLeaveBalance());
  }, [dispatch]);

  // Build monthly map
  useEffect(() => {
    const map: Record<string, AttendanceRecord> = {};
    monthlyRecords.forEach((rec) => {
      const d = new Date(rec.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      map[key] = rec;
    });
    setMonthlyMap(map);
  }, [monthlyRecords]);

  const openModalForUser = (userId: string) => {
    setSelectedUser(userId);
    setIsModalOpen(true);
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    setSelectedMonth(month);
    setSelectedYear(year);
    dispatch(fetchMonthlyReport({ month, year, userId }));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  // Stats calculations
  const presentCount = allTodayRecords.filter(
    (r) => r.attendance?.firstSessionStatus?.toLowerCase() === "present"
  ).length;

  const absentCount = allTodayRecords.length - presentCount;
  const totalUsers = allTodayRecords.length;
  const attendanceRate = totalUsers ? (presentCount / totalUsers) * 100 : 0;

  return (
    <>
      {["admin", "hr", "sh", "bdm"].includes(role ?? "") && (
        <ManagerPendingApplications />
      )}
      <div className="mb-8">
        {["admin", "hr"].includes(role ?? "") && <HrDashboardStack />}
      </div>
      <div className="min-h-screen ">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4">
            <div className="rounded-2xl p-4 max-h-28 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    Present Today
                  </p>
                  <p className="text-lg font-bold text-green-teal">
                    {presentCount}
                  </p>
                  <div className="flex items-center text-xs text-green-teal">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    <span>Active</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-br from-green-teal to-green-500 rounded-xl shadow-lg">
                  <UserCheck className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>

            <div className=" rounded-2xl shadow-md p-4  max-h-28 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    Absent Today
                  </p>
                  <p className="text-lg font-bold text-rose-600">
                    {absentCount}
                  </p>
                  <div className="flex items-center text-xs text-red-primary">
                    <Activity className="h-3 w-3 mr-1" />
                    <span>Missing</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-br from-red-500 to-red-500 rounded-xl shadow-lg">
                  <UserX className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>

            <div className=" rounded-2xl shadow-md p-4  max-h-28 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    Total Users
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    {totalUsers}
                  </p>
                  <div className="flex items-center text-xs text-blue-600">
                    <Users className="h-3 w-3 mr-1" />
                    <span>Registered</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg">
                  <Users className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>

            <div className=" rounded-2xl shadow-md p-4 max-h-28 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    Attendance Rate
                  </p>
                  <p className="text-lg font-bold text-indigo-600">
                    {attendanceRate.toFixed(1)}%
                  </p>
                  <div className="flex items-center text-xs text-indigo-600">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    <span>Overall</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-lg">
                  <Activity className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-md flex items-center gap-4">
            <SearchInput
              placeholder="Search by name…"
              className="border text-xs rounded p-2 w-full outline-none"
              onSearch={(term) => {
                setSearchTerm(term);
                dispatch(
                  fetchAllTodayAttendance({ search: term, date: selectedDate })
                );
              }}
            />
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="border text-xs rounded p-2"
            />
          </div>
          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-md">
            {!loading && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Punch In
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Punch Out
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        In Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Out Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Map
                      </th>
                    </tr>
                  </thead>
                  <tbody className=" divide-y divide-gray-200">
                    {allTodayRecords.map((rec) => {
                      const att = rec.attendance;
                      const punchInTime = att?.punchIn ? (
                        new Date(att.punchIn).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      ) : (
                        <Activity className="h-3 w-3 mr-1 text-gray-400" />
                      );
                      const punchOutTime = att?.punchOut ? (
                        new Date(att.punchOut).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      ) : (
                        <Activity className="h-3 w-3 mr-1 text-gray-400" />
                      );
                      const inLoc = att?.punchInLocation ? (
                        `${att.punchInLocation.lat.toFixed(
                          4
                        )}, ${att.punchInLocation.lng.toFixed(4)}`
                      ) : (
                        <Activity className="h-3 w-3 mr-1 text-gray-400" />
                      );

                      const outLoc = att?.punchOutLocation ? (
                        `${att.punchOutLocation.lat.toFixed(
                          4
                        )}, ${att.punchOutLocation.lng.toFixed(4)}`
                      ) : (
                        <Activity className="h-3 w-3 mr-1 text-gray-400" />
                      );
                      const firstSessionStatus =
                        att?.firstSessionStatus ?? "Absent";

                      return (
                        <tr
                          key={rec.user._id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                          onClick={() => openModalForUser(rec.user._id ?? "")}
                        >
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 flex-shrink-0">
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-blue-700">
                                    {rec.user.username.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="text-xs font-medium text-gray-900">
                                  {rec.user.username}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <span
                              className={getStatusBadge(firstSessionStatus)}
                            >
                              {getStatusIcon(firstSessionStatus)}
                              {firstSessionStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-900">
                            <div className="flex items-center">
                              {att?.punchIn && (
                                <Clock className="h-4 w-4 mr-2 text-green-teal" />
                              )}
                              {punchInTime}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-900">
                            <div className="flex items-center">
                              {att?.punchOut && (
                                <Clock className="h-4 w-4 mr-2 text-rose-600" />
                              )}
                              {punchOutTime}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                            <div className="flex items-center">
                              {att?.punchInLocation && (
                                <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                              )}

                              <span className="truncate max-w-32">{inLoc}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                            <div className="flex items-center">
                              {att?.punchOutLocation && (
                                <MapPin
                                  className="h-4 w-4 mr-2 "
                                  color="#3B82F6"
                                />
                              )}
                              <span className="truncate max-w-32">
                                {outLoc}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <BaseButton
                              text="Show Map"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMapUserId(rec.user._id);
                                setIsMapOpen(true);
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {selectedUser && (
          <AttendanceCalendar
            year={selectedYear}
            month={selectedMonth}
            monthlyMap={monthlyMap}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            selectedUser={selectedUser}
            isModalOpen={isModalOpen}
            onClose={closeModal}
          />
        )}
        {mapUserId && (
          <ShowUserRouteMap
            isOpen={isMapOpen}
            userId={mapUserId}
            onClose={() => {
              setMapUserId(null);
              setIsMapOpen(false);
            }}
          />
        )}
      </div>
    </>
  );
};

export default HomeTab;
